/**
 * Does this page get a Frame picker tab, and is the picker switched on? — FRAME_PICKER_DESIGN.md
 * §6.1 "Gate".
 *
 * TWO INDEPENDENT QUESTIONS, kept apart because they fail differently (the same split as
 * `components/studio/studio-gate.ts`). The PAGE question is a fact about the route and is known
 * during static export, so the tab strip renders identically on the server and on the client and
 * costs no layout shift. The FLAG question is a fact about the fleet, arrives later, and can only
 * ever TAKE the tab away.
 *
 * THE PAGE HALF. §6.1: "a lookup helper over `convertOptionsV2.properties` (`fromMedia ===
 * 'VIDEO' && toMedia === 'IMAGE'`) and `pageData.toType ∉ {gif, apng}`". `properties` is an
 * ARRAY of `{propertyName, propertyValue}` (design §0 "Gate" row; `ssr-data/build/*.json`), not
 * an object, so it is searched rather than indexed. Measured 2026-09-12: 592 pages carry
 * `fromMedia=VIDEO, toMedia=IMAGE`; the 74 GIF/APNG targets route to `VideoConversionStrategy`
 * and must not show the tab; the 32 PRESENTATION→IMAGE pages fail the `fromMedia` test; the
 * animated-image INPUT pages (`convert-gif-to-jpg`…) are `fromMedia=IMAGE` and failed it too
 * (§13: v2) -- until R14 (BUG-003): behind `ANIMATED_IMAGE_PICKER` those three inputs (gif, webp,
 * apng) to a still target light the tab as well. That leaves the 518 video pages plus those.
 *
 * This module imports nothing at all — not even a type. It is reached from
 * `converter-page-base`, which is in the static entry graph, and a service edge there is the
 * crash `file-convert.component.tsx:813-822` records.
 */

/** The picker's tab value, re-stated so the page base need not import the picker's URL module. */
export const PICKER_TAB = 'picker';

/** The tab label and its pill (§6.1 "the tab (with a NEW pill)"). */
export const PICKER_TAB_LABEL = 'Frame picker';
export const PICKER_TAB_BADGE = 'NEW';

/** The image targets the picker never serves: they are animations, not stills (§1). */
export const PICKER_EXCLUDED_TO_TYPES: ReadonlySet<string> = new Set(['gif', 'apng']);

/**
 * R14 (slice C, BUG-003): animated-image INPUT pages (`convert-gif-to-jpg`, `convert-webp-to-jpg`, `convert-apng-to-png`,
 * `fromMedia=IMAGE`) get the tab too. Requires ironman ≥ `1ac91e8d` (2026-09-23): it routes animated images into
 * `VideoToImageStrategy` (the picker's `screenshotTime` / range keys pick real frames; measured: the oracle GIF's 10 outputs
 * = frames 0…9) and emits `duration` on the image probe. This constant is the ONE place to turn that off again: `false`
 * hides the tab on those pages, keeps `isVideoUpload` / `isVideoFile` to videos, and turns the refusal back into
 * "The Frame picker can’t open clip.gif yet…" (`copy.ts#animatedNotYet`). Both values are unit-tested. R14Q-01: the switch
 * is necessary, not sufficient -- the page's tree must also carry the frame-selection group (`treeHasFrameSelection`); until
 * xconfig widens that group's `hidden` clause the animated pages behave as switch-off, honestly.
 */
export const ANIMATED_IMAGE_PICKER = true;

/** The animated image inputs the picker can open (behind `ANIMATED_IMAGE_PICKER`). */
export const PICKER_ANIMATED_FROM_TYPES: ReadonlySet<string> = new Set(['gif', 'webp', 'apng']);

/** The still-image converter that exists for each animated input -- the page the refusal / wrong-page notice links to. */
export const ANIMATED_STILL_TARGETS: Readonly<Record<string, string>> = { gif: 'jpg', webp: 'jpg', apng: 'png' };

/** Is `ext` (with or without a dot, any case) one of the animated image inputs? */
export function isAnimatedImageExtension(ext: unknown): boolean {
    return PICKER_ANIMATED_FROM_TYPES.has(normaliseToType(ext));
}

/**
 * `/convert-gif-to-jpg` (`withTab`: `?tab=picker` -- the page whose picker OPENS the file, once the switch is on), or null for
 * an extension that is not an animated input.
 */
export function animatedStillPagePath(ext: unknown, withTab: boolean): string | null {
    const e = normaliseToType(ext);
    const target = ANIMATED_STILL_TARGETS[e];
    if (!target) return null;
    return `/convert-${e}-to-${target}${withTab ? `?tab=${PICKER_TAB}` : ''}`;
}

/**
 * The shape of `pageData` this module reads. Structural, so a test needs no PageData. `fromType` is the route's input
 * word(s) -- `["gif"]` on `convert-gif-to-jpg` (ssr-data/build), read for the animated-image pages; the options'
 * `fromExtension` property is the fallback.
 */
export interface PickerPageFacts {
    toType?: string;
    fromType?: unknown;
}

/**
 * The shape of `convertOptionsV2` this module reads — the properties ARRAY and, since R14Q-01, the option tree's top-level
 * `options` (groups), so the animated-image gate can see whether THIS page's tree can carry a frame choice at all.
 */
export interface PickerOptionsFacts {
    properties?: Array<{ propertyName?: string; propertyValue?: unknown }> | null;
    options?: unknown[] | null;
}

/** The frame-selection GROUP and the property it writes (xconfig `video-to-image-frame-selection.options.ts`). */
export const FRAME_SELECTION_GROUP_ID = 'VIDEO_TO_IMAGE_FRAME_SELECTION';
export const FRAME_SELECTION_PROPERTY = 'videoToImageFrameSelection';

/**
 * R14Q-01 (P1): can this page's option tree CARRY a frame choice? Every page's tree holds the `VIDEO_TO_IMAGE_FRAME_SELECTION`
 * group (one shared template), but its `hidden` clause is `¬(fromMedia ∈ {VIDEO, PRESENTATION} ∧ toMedia = IMAGE ∧
 * toExtension ∉ {GIF, APNG})` -- on a `fromMedia=IMAGE` page the group is hidden, a hidden group summarises to NOTHING
 * (`GroupViewState.summarize`), and Convert sends the plain IMAGE→IMAGE options: QA's GIF export was frame 0 for "Frame 5 of 10".
 * So the animated-image picker lights only where the group is present AND not hidden for the page's `properties`; when xconfig
 * widens the clause the tab lights with no code change. The clause is evaluated here with the same operators as
 * `convert-options-v2/models/option-requirement.ts#isHidden` (this module must import nothing -- it is in the static entry graph);
 * a property the page does not name (`toExtension`) reads as undefined there and here alike.
 */
export function treeHasFrameSelection(options: PickerOptionsFacts | null | undefined): boolean {
    const group = findFrameSelectionGroup(options?.options);
    if (!group) return false;
    const results: Record<string, unknown> = {};
    for (const p of options?.properties ?? []) if (p && typeof p.propertyName === 'string') results[p.propertyName] = p.propertyValue;
    const hidden = (group as { hidden?: unknown }).hidden;
    if (!Array.isArray(hidden) || hidden.length === 0) return true;
    return !hidden.every((req) => evaluateRequirement(req, results));
}

function findFrameSelectionGroup(nodes: unknown, depth = 0): Record<string, unknown> | null {
    if (!Array.isArray(nodes) || depth > 3) return null;
    for (const n of nodes) {
        if (!n || typeof n !== 'object') continue;
        const node = n as Record<string, unknown>;
        const props = node.propertyNames;
        if (node.id === FRAME_SELECTION_GROUP_ID || (Array.isArray(props) && props.includes(FRAME_SELECTION_PROPERTY))) return node;
        const inner = findFrameSelectionGroup(node.options, depth + 1);
        if (inner) return inner;
    }
    return null;
}

/** `option-requirement.ts#evaluateSingleRequirement`, restated: `$eq/$neq/$nin/$in/$gt/$lt/$and/$or/$not`; an unknown shape is false. */
function evaluateRequirement(req: unknown, results: Record<string, unknown>): boolean {
    if (!req || typeof req !== 'object') return false;
    const r = req as Record<string, any>;
    const atom = (k: string) => r[k] as { propertyName: string; propertyValue: any };
    if ('$eq' in r) return results[atom('$eq').propertyName] == atom('$eq').propertyValue; // eslint-disable-line eqeqeq
    if ('$neq' in r) return results[atom('$neq').propertyName] != atom('$neq').propertyValue; // eslint-disable-line eqeqeq
    if ('$nin' in r) { const { propertyName, propertyValue } = atom('$nin'); return !Array.isArray(propertyValue) || !propertyValue.includes(results[propertyName]); }
    if ('$in' in r) { const { propertyName, propertyValue } = atom('$in'); return Array.isArray(propertyValue) && propertyValue.includes(results[propertyName]); }
    if ('$gt' in r) return (results[atom('$gt').propertyName] as any) > atom('$gt').propertyValue;
    if ('$lt' in r) return (results[atom('$lt').propertyName] as any) < atom('$lt').propertyValue;
    if ('$and' in r) return Array.isArray(r.$and) && r.$and.every((x: unknown) => evaluateRequirement(x, results));
    if ('$or' in r) return Array.isArray(r.$or) && r.$or.some((x: unknown) => evaluateRequirement(x, results));
    if ('$not' in r) return !evaluateRequirement(r.$not, results);
    return false;
}

/**
 * The same question at RUN time, off the live options context's summary (`FramePickerHost.optionsContext.getSummary()`): a
 * hidden group writes no key, so the key's presence IS the capability. The root reads this for its accept list and its copy.
 */
export function summaryCarriesFrameSelection(summary: Record<string, unknown> | null | undefined): boolean {
    return !!summary && Object.prototype.hasOwnProperty.call(summary, FRAME_SELECTION_PROPERTY);
}

/** The provenance-carrying settings emission, restated structurally for the same reason. */
export interface PickerFlagEmission {
    flags?: { framePicker?: boolean } | null;
    source?: 'props' | 'api';
}

/** One `{propertyName, propertyValue}` lookup, upper-cased so `video`/`VIDEO` agree. */
function propertyOf(options: PickerOptionsFacts | null | undefined, name: string): string {
    const list = options?.properties;
    if (!Array.isArray(list)) return '';
    const hit = list.find((p) => p && p.propertyName === name);
    return String(hit?.propertyValue == null ? '' : hit.propertyValue).trim().toUpperCase();
}

function normaliseToType(toType: unknown): string {
    const raw = String(toType == null ? '' : toType).trim().toLowerCase();
    return raw.charAt(0) === '.' ? raw.slice(1) : raw;
}

/** The page's input extension: `pageData.fromType` (an array of words or one word), else the options' `fromExtension`. */
function fromTypeOf(pageData: PickerPageFacts, options: PickerOptionsFacts | null | undefined): string {
    const raw = pageData.fromType;
    const first = Array.isArray(raw) ? raw[0] : raw;
    const word = normaliseToType(first);
    return word || propertyOf(options, 'fromExtension').toLowerCase();
}

/**
 * Is this route a video→still converter -- or, with `ANIMATED_IMAGE_PICKER` (R14, BUG-003), an animated-image→still one?
 *
 * An absent or empty properties array answers no: "we could not tell" must not light a tab. `animated` is the switch,
 * a parameter so both values are tested; callers never pass it.
 */
export function pageAcceptsPicker(
    pageData: PickerPageFacts | null | undefined,
    options: PickerOptionsFacts | null | undefined,
    animated: boolean = ANIMATED_IMAGE_PICKER,
): boolean {
    if (!pageData) return false;
    if (propertyOf(options, 'toMedia') !== 'IMAGE') return false;
    if (PICKER_EXCLUDED_TO_TYPES.has(normaliseToType(pageData.toType))) return false;
    const fromMedia = propertyOf(options, 'fromMedia');
    if (fromMedia === 'VIDEO') return true;
    // R14Q-01: an animated-image page lights only when its own tree can carry the frame choice (`treeHasFrameSelection`).
    return animated && fromMedia === 'IMAGE' && PICKER_ANIMATED_FROM_TYPES.has(fromTypeOf(pageData, options)) && treeHasFrameSelection(options);
}

/**
 * Does the fleet say the picker is off? Only an API-sourced explicit `false` does (§6.1).
 *
 * `undefined` is not `false`: an edge on a settings document written before the flag existed,
 * and every props-sourced emission, both mean "we have not been told otherwise". The backend
 * flag defaults TRUE (§11.0 R8), which is why this reads as "hide on an explicit false".
 */
export function pickerFlagHides(emission: PickerFlagEmission | null | undefined): boolean {
    return emission?.source === 'api' && emission?.flags?.framePicker === false;
}

/** Both halves. The tab is visible unless one of them says no. */
export function pickerTabVisible(
    pageData: PickerPageFacts | null | undefined,
    options: PickerOptionsFacts | null | undefined,
    emission: PickerFlagEmission | null | undefined,
): boolean {
    return pageAcceptsPicker(pageData, options) && !pickerFlagHides(emission);
}
